import { useCallback, useMemo, useState, type RefObject } from "react";
import { UserProductImageSource } from "@apis/generated/openapi";
import type { ClipUploadFile } from "@components/ClipUpload/ClipUpload";
import type { ProductFormState, ProductImageFile, ProductImageResponse } from "./types";

const getMeta = (file: ClipUploadFile): ProductImageResponse => {
  const raw = file.response as ProductImageResponse | undefined;
  return {
    assetId: raw?.assetId,
    description: raw?.description ?? "",
    aiAnalyzed: !!raw?.aiAnalyzed,
    source: raw?.source ?? UserProductImageSource.USER,
  };
};

/** 序列化表单 + 图片为可比较快照（忽略卖点本地 id） */
export const serializeProductSnapshot = (form: ProductFormState, imageFiles: ProductImageFile[]): string =>
  JSON.stringify({
    sourceType: form.sourceType,
    sourceProductId: form.sourceProductId,
    sourceUrl: form.sourceUrl,
    country: form.country,
    title: form.title,
    category: form.category,
    categorySource: form.categorySource,
    sellingPoints: form.sellingPoints.map((sp) => ({ content: sp.content, checked: sp.checked })),
    images: imageFiles.map((f) => {
      const meta = getMeta(f);
      return { url: f.url ?? "", description: meta.description ?? "" };
    }),
  });

export interface ProductFormSnapshot {
  form: ProductFormState;
  imageFiles: ProductImageFile[];
}

export const useProductFormDirty = (
  form: ProductFormState,
  imageFiles: ProductImageFile[],
  formRef: RefObject<ProductFormState>,
  imageFilesRef: RefObject<ProductImageFile[]>,
) => {
  const [baseline, setBaseline] = useState<string | null>(null);

  const captureBaseline = useCallback(
    (snapshot?: ProductFormSnapshot) => {
      const nextForm = snapshot?.form ?? formRef.current;
      const nextFiles = snapshot?.imageFiles ?? imageFilesRef.current;
      if (!nextForm || !nextFiles) return;
      setBaseline(serializeProductSnapshot(nextForm, nextFiles));
    },
    [formRef, imageFilesRef],
  );

  const resetBaseline = useCallback(() => {
    setBaseline(null);
  }, []);

  const isDirty = useMemo(() => {
    if (!baseline) return false;
    return serializeProductSnapshot(form, imageFiles) !== baseline;
  }, [form, imageFiles, baseline]);

  return { isDirty, captureBaseline, resetBaseline };
};
